// Stempelt `sentAt` in den PX-Connect-Schadenbericht-Draft, sobald die aus ihm
// abgeleitete Korrespondenz gespeichert wurde (ADR 0006). Bewusst imperativ (kein
// Query-Hook), damit der CorrespondenceModal ihn nur bei vorhandenem Marker aufruft.
import { useMutation } from '@apollo/client';
import { GetProjectDocument, UpdateProjectMetadataDocument, type Projekt } from '~/gql/ucpw/graphql';
import { client } from '~/apollo';
import {
    assertProjectMetadataSaved,
    mergePxConnectSchadenberichtMetadata,
    mergePxConnectVersandMetadata,
    pxConnectDraftFromMetadata,
    type PxBearbeitungsstand,
} from '~/meta/data/px-connect';
import log from '~/log';

export const usePxConnectSchadenberichtSent = () => {
    const context = { clientName: 'ucpw' };
    const [updateProjectMetadata] = useMutation(UpdateProjectMetadataDocument, { context });

    /**
     * Stempelt `sentAt` auf den aktuellen Zeitpunkt (zuletzt versendet). Wird nur beim
     * Speichern einer frisch aus dem Schadenbericht vorbereiteten Korrespondenz aufgerufen
     * (Marker `__pxSchadenberichtProjektId`), daher spiegelt der Wert das jeweils letzte
     * Versenden — auch nach einer zwischenzeitlichen Bearbeitung des Berichts. Holt Projekt
     * frisch (network-only), um Version und aktuelle Metadaten für das Merge zu haben.
     *
     * Mit `versand` wird im selben Schreibvorgang der Bearbeitungsstand dieser
     * Korrespondenz festgehalten (UCPWO-410: auch unvollständige Berichte gehen raus).
     */
    const markSent = async (
        projektId: number,
        versand?: { korrespondenzId?: number; stand?: PxBearbeitungsstand }
    ): Promise<void> => {
        try {
            const { data } = await client.query({
                query: GetProjectDocument,
                variables: { id: projektId },
                context,
                fetchPolicy: 'network-only',
            });
            const project = data?.item?.item as Projekt | undefined;
            if (!project) return;
            const draft = pxConnectDraftFromMetadata(project.metadata);
            // Kein Draft (bereits gelöscht) → nichts zu stempeln. Ein bereits gesetztes
            // `sentAt` wird bewusst überschrieben (erneutes Versenden aktualisiert „versendet am").
            if (!draft?.answers?.length) return;
            const withSentAt = mergePxConnectSchadenberichtMetadata(project.metadata, {
                sentAt: new Date().toISOString(),
            });
            const result = await updateProjectMetadata({
                variables: {
                    projektId,
                    version: project.version,
                    forceOverwrite: true,
                    metadata:
                        versand?.korrespondenzId && versand.stand
                            ? mergePxConnectVersandMetadata(
                                  withSentAt,
                                  versand.korrespondenzId,
                                  versand.stand
                              )
                            : withSentAt,
                },
            });
            assertProjectMetadataSaved(result);
        } catch (err) {
            // „versendet am" ist ein Nice-to-have-Stempel — ein Fehler hier darf das
            // erfolgreiche Speichern der Korrespondenz nicht rückwirkend als Fehler zeigen.
            log.error('PxConnectSchadenbericht.markSent', err);
        }
    };

    return { markSent };
};
